"use client";

import React from "react";
import axios from "axios";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  useToast,
  Text,
  Box,
  Code,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import { CheckCircle, Copy } from "lucide-react";
import { supabase } from "../../supabaseClient";

interface ConnectVoiceNumberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ConnectFormData {
  twilio_account_sid: string;
  twilio_auth_token: string;
  phone_number: string;
  nickname: string;
}

interface ConnectedPhoneNumber {
  phone_number: string;
  sip_trunk_domain: string;
  sip_credential_username: string;
  sip_credential_password: string;
}

export function ConnectVoiceNumberDialog({
  isOpen,
  onClose,
  onSuccess,
}: ConnectVoiceNumberDialogProps) {
  const toast = useToast();
  const [step, setStep] = React.useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [connectedNumber, setConnectedNumber] =
    React.useState<ConnectedPhoneNumber | null>(null);

  const [formData, setFormData] = React.useState<ConnectFormData>({
    twilio_account_sid: "",
    twilio_auth_token: "",
    phone_number: "",
    nickname: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setStep(1);
    setConnectedNumber(null);
    setFormData({
      twilio_account_sid: "",
      twilio_auth_token: "",
      phone_number: "",
      nickname: "",
    });
    onClose();
  };

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value).then(() => {
      toast({
        title: `${label} copied`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    });
  };

  const handleSubmit = async () => {
    if (
      !formData.twilio_account_sid ||
      !formData.twilio_auth_token ||
      !formData.phone_number
    ) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL || ""}/api/voice/import-phone-number`,
        {
          phone_number: formData.phone_number,
          twilio_account_sid: formData.twilio_account_sid,
          twilio_auth_token: formData.twilio_auth_token,
          nickname: formData.nickname || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );

      const phoneNumber: ConnectedPhoneNumber = response.data?.phoneNumber;
      setConnectedNumber(phoneNumber);
      setStep(2);
      onSuccess();
    } catch (error: any) {
      console.error("Error connecting phone number:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : error?.response?.data?.message || "An unexpected error occurred";
      toast({
        title: "Error connecting phone number",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent bg="white">
        {step === 1 ? (
          <>
            <ModalHeader color="gray.800">
              Connect Your Twilio Number
            </ModalHeader>
            <ModalCloseButton color="gray.800" />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel color="gray.800">Twilio Account SID</FormLabel>
                  <Input
                    name="twilio_account_sid"
                    value={formData.twilio_account_sid}
                    onChange={handleChange}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    bg="white"
                    color="gray.800"
                    borderColor="gray.200"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.800">Twilio Auth Token</FormLabel>
                  <Input
                    name="twilio_auth_token"
                    value={formData.twilio_auth_token}
                    onChange={handleChange}
                    type="password"
                    placeholder="Your Twilio Auth Token"
                    bg="white"
                    color="gray.800"
                    borderColor="gray.200"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.800">Phone Number</FormLabel>
                  <Input
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="+1XXXXXXXXXX"
                    bg="white"
                    color="gray.800"
                    borderColor="gray.200"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color="gray.800">
                    Nickname{" "}
                    <Text as="span" color="gray.500">
                      (Optional)
                    </Text>
                  </FormLabel>
                  <Input
                    name="nickname"
                    value={formData.nickname}
                    onChange={handleChange}
                    placeholder="Enter a nickname"
                    bg="white"
                    color="gray.800"
                    borderColor="gray.200"
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="white"
                textColor="gray.800"
                variant="ghost"
                mr={3}
                onClick={handleClose}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="primary"
                bg="primary.800"
                _hover={{ bg: "primary.900" }}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Connecting..."
              >
                Connect Number
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader color="gray.800">
              <HStack spacing={2}>
                <Icon as={CheckCircle} color="green.500" boxSize={6} />
                <Text>Twilio setup complete!</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton color="gray.800" />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Text color="gray.600" fontSize="sm">
                  One final step: import your number into the voice platform.
                </Text>

                <Box
                  bg="blue.50"
                  borderRadius="md"
                  p={4}
                  border="1px solid"
                  borderColor="blue.100"
                >
                  <Text fontSize="sm" color="blue.800" fontWeight="medium" mb={3}>
                    Go to your voice platform dashboard → Phone Numbers → Connect
                    SIP
                  </Text>

                  <VStack spacing={3} align="stretch">
                    <Box>
                      <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={1}>
                        Termination URI
                      </Text>
                      <HStack spacing={2}>
                        <Code
                          p={2}
                          borderRadius="md"
                          fontSize="sm"
                          display="block"
                          wordBreak="break-all"
                          flex={1}
                        >
                          {connectedNumber?.sip_trunk_domain || ""}
                        </Code>
                        <IconButton
                          aria-label="Copy Termination URI"
                          icon={<Copy size={14} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() =>
                            handleCopy(
                              connectedNumber?.sip_trunk_domain || "",
                              "Termination URI"
                            )
                          }
                        />
                      </HStack>
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={1}>
                        SIP Username
                      </Text>
                      <HStack spacing={2}>
                        <Code
                          p={2}
                          borderRadius="md"
                          fontSize="sm"
                          display="block"
                          wordBreak="break-all"
                          flex={1}
                        >
                          {connectedNumber?.sip_credential_username || ""}
                        </Code>
                        <IconButton
                          aria-label="Copy SIP Username"
                          icon={<Copy size={14} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() =>
                            handleCopy(
                              connectedNumber?.sip_credential_username || "",
                              "SIP Username"
                            )
                          }
                        />
                      </HStack>
                    </Box>

                    <Box>
                      <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={1}>
                        SIP Password
                      </Text>
                      <HStack spacing={2}>
                        <Code
                          p={2}
                          borderRadius="md"
                          fontSize="sm"
                          display="block"
                          wordBreak="break-all"
                          flex={1}
                        >
                          {connectedNumber?.sip_credential_password || ""}
                        </Code>
                        <IconButton
                          aria-label="Copy SIP Password"
                          icon={<Copy size={14} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() =>
                            handleCopy(
                              connectedNumber?.sip_credential_password || "",
                              "SIP Password"
                            )
                          }
                        />
                      </HStack>
                    </Box>
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="primary"
                bg="primary.800"
                _hover={{ bg: "primary.900" }}
                onClick={handleClose}
              >
                Done
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
